'use client';

import { useState } from 'react';
import { tv } from 'tailwind-variants';
import { faFlaskVial } from '@fortawesome/pro-light-svg-icons';
import {
  Laboratory,
  SimilarLaboratories,
  SimilarLaboratoriesResponse,
} from '@medprev-web-app/api-graphql-client';

import { getSimilarLaboratoriesByLaboratoryId } from '@/app/(main)/(pages)/(institucional)/(index)/laboratorios/_services/get-similiar-laboratories-by-laboratory-id.service';
import { formatToTitleCase } from '@/utils/formatters/format-to-title-case';
import { Link } from '@/components/ui/link/link.component';
import { EntityAvatar } from '@/components/ui/avatar/entity-avatar';
import { EntityTypeEnum } from '@/types/enums/entity-type.enum';
import { Button } from '@/components/ui/button/button.component';
import { formatDistance } from '@/utils/formatters/format-distance';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import {
  parseSelectedExamsParam,
  SelectedExamsParam,
} from '@/app/(main)/(pages)/(institucional)/(index)/laboratorios/_hooks/use-selected-exams-coverage.hook';
import { buildPathWithQuery } from '@/utils/persisted-params.util';

export type LaboratoryOthersAlign = 'start' | 'center';

const othersLaboratoriesStyles = tv({
  slots: {
    description: 'text-gray-500 text-base font-normal mb-2',
  },

  variants: {
    align: {
      start: { description: 'text-left' },
      center: { description: 'text-center' },
    },
  },

  defaultVariants: {
    align: 'start',
  },
});

type LaboratoryOtherLaboratoriesProps = {
  initialFetchResponse: SimilarLaboratoriesResponse;
  laboratory: Laboratory;
  align?: LaboratoryOthersAlign;
  selectedExams?: SelectedExamsParam;
};

export function LaboratoryOthersLaboratories({
  laboratory,
  initialFetchResponse,
  align = 'start',
  selectedExams,
}: LaboratoryOtherLaboratoriesProps) {
  const { description } = othersLaboratoriesStyles({ align });
  const { total: totalLaboratories } = initialFetchResponse;
  const [laboratories, setLaboratories] = useState<
    typeof initialFetchResponse.laboratories
  >([]);
  const [page, setPage] = useState(
    initialFetchResponse.pagination.page as number,
  );
  const [loading, setLoading] = useState(false);

  const loadMore = async () => {
    setLoading(true);
    const list = await getSimilarLaboratoriesByLaboratoryId(
      laboratory.id as string,
      page + 1,
    );

    if (list) {
      setLaboratories([...laboratories, ...(list?.laboratories ?? [])]);
      setPage(page + 1);
      setLoading(false);
    }
  };

  const laboratoryLabel = () => {
    if (laboratory.main.socialName) {
      return formatToTitleCase(laboratory.main.socialName);
    }

    return formatToTitleCase(laboratory.main.legalName as string);
  };

  const canLoadMore = page * 5 < initialFetchResponse.total;

  const examSlugs = parseSelectedExamsParam(selectedExams);

  const buildLaboratoryHref = (slug: SimilarLaboratories['slug']) => {
    const path = `/laboratorios/${slug}/`;

    return examSlugs.length ? buildPathWithQuery(path, { exames: examSlugs }) : path;
  };

  if (totalLaboratories === 0) {
    return null;
  }

  return (
    <section>
      <p className={description()}>
        {totalLaboratories === 1
          ? `${laboratoryLabel()} possui mais 1 posto de coleta`
          : `${laboratoryLabel()} possui mais ${totalLaboratories} postos de coleta`}
      </p>
      <div>
        {initialFetchResponse.laboratories.map((lab) => {
          return (
            <Link
              key={lab.id}
              prefetch={false}
              href={buildLaboratoryHref(lab.slug)}
              data-analytics-event={AnalyticsEnum.CLICK_SEE_OTHER_LABORATORIES_LABORATORY_PAGE}
              data-analytics-properties={JSON.stringify({
                laboratory_id: laboratory.id,
                laboratory_name: laboratoryLabel(),
              })}
            >
              <div className="py-2 flex flex-row gap-4 items-center">
                <div className="self-start">
                  <EntityAvatar
                    src={laboratory.main.avatar}
                    alt={`Foto de perfil do laboratório ${[
                      laboratoryLabel(),
                      formatToTitleCase(lab.name as string),
                    ].join(' - ')}`}
                    width={64}
                    height={64}
                    type={EntityTypeEnum.LABORATORY_COLLECT_POINT}
                  />
                </div>
                <div>
                  <p className="text-green-500 text-base font-bold leading-6 hover:underline break-words whitespace-pre-line">
                    {[
                      laboratoryLabel(),
                      formatToTitleCase(lab.name as string),
                    ].join(' - ')}
                  </p>
                  {lab.distance && (
                    <p className="text-gray-500 text-sm font-light">{`a ${formatDistance(lab.distance)}km`}</p>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
        {laboratories.map((lab) => {
          return (
            <Link key={lab.id} prefetch={false} href={buildLaboratoryHref(lab.slug)}>
              <div className="py-2 flex flex-row gap-4 items-center">
                <div className="self-start">
                  <EntityAvatar
                    src={laboratory.main.avatar}
                    alt={`Foto de perfil do laboratório ${[
                      laboratoryLabel(),
                      formatToTitleCase(lab.name as string),
                    ].join(' - ')}`}
                    width={64}
                    height={64}
                    type={EntityTypeEnum.LABORATORY_COLLECT_POINT}
                  />
                </div>

                <div>
                  <p className="text-green-500 text-base font-bold leading-6 hover:underline">
                    {' '}
                    {[
                      laboratoryLabel(),
                      formatToTitleCase(lab.name as string),
                    ].join(' - ')}
                  </p>
                  {lab.distance && (
                    <p className="text-gray-500 text-sm font-light">{`a ${formatDistance(lab.distance)}km`}</p>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
        {canLoadMore && (
          <Button
            isLoading={loading}
            className="w-full mt-3 h-12 mb-2"
            variant="secondary"
            startIcon={faFlaskVial}
            onClick={loadMore}
            disabled={loading}
            data-analytics-event={AnalyticsEnum.LABORATORY_PROFILE_CLICK_SHOW_MORE_LOCATIONS}
          >
            Mostrar mais postos
          </Button>
        )}
      </div>
    </section>
  );
}
